<template>
  <div class="image-preview">
    <div v-if="imageUrl" class="preview-container">
      <div ref="imageContainer" class="image-container">
        <vue-draggable-resizable v-for="(layer,i) in overlaySettings.layers" :key="i" :handles="handles" :x="(layer.left / 100) * parent.width" :y="(layer.top / 100) * parent.height" :parent="false" :w="(layer.width / 100) * parent.width" :h="(layer.height / 100) * parent.height" :min-height="30" :active="true" :z="1000" :style="`background-color: ${hexToRgba(layer.bgColor, layer.bgOpacity)}; color: ${layer.textColor}; font-size: ${layer.fontSize}px`" @dragstop="(left, top) => SET_LAYER_DRAG({ layer, left, top, parent })" @resizestop="(left, top, width, height) => SET_LAYER_RESIZE({ layer, left, top, width, height, parent })">
          <p>{{layer.text}}</p>
        </vue-draggable-resizable>
        <img :src="imageUrl" alt="Preview" class="preview-image" @load="updateParentSize" />

        <!-- Text Overlay -->
        <div
          ref="overlayText"
          class="overlay-text"
          :class="[overlayClass, animationClass]"
          :style="overlayStyle"
          @mousedown.stop="startDrag"
        >
          {{ displayText }}
        </div>

        <!-- Move handle indicator with tooltip -->
        <div
          v-if="!previewMode"
          class="move-indicator"
          :style="{
            left: `${overlaySettings.position.x}%`,
            top: `${overlaySettings.position.y}%`,
          }"
        >
          <div class="handle-tooltip">Drag to move</div>
        </div>
      </div>
      <!-- Preview Mode Controls -->
      <div v-if="previewMode" class="preview-controls">
        <button class="btn btn-sm btn-outline-primary" @click="exitPreviewMode">
          <i class="fas fa-pencil-alt mr-1"></i> Edit Text Overlay
        </button>
      </div>
      <!-- GIF Animation Preview -->
      <div v-if="shouldPreviewGif && isGifMode && overlaySettings.animation.enabled" class="animation-preview-overlay">
        <div class="animation-message">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <div class="mt-2">Previewing Animation</div>
        </div>
      </div>
    </div>
    <div v-else class="no-image">
      <p>No image selected</p>
      <i class="far fa-image no-image-icon"></i>
    </div>
  </div>
</template>

<script>
import { toPng } from 'html-to-image';
import {mapMutations, mapState} from 'vuex';
import VueDraggableResizable from 'vue-draggable-resizable'
import 'vue-draggable-resizable/dist/VueDraggableResizable.css'
export default {
  name: 'SimpleImagePreview',
  components:{
    VueDraggableResizable
  },
  props: {
    hideHandles:{
      type:Boolean,
      default:false
    },
    imageUrl: {
      type: String,
      default: null
    },
    overlaySettings: {
      type: Object,
      default: () => ({
        text: 'Sample Text',
        fontSize: 24,
        style: 'white',
        shape: 'rectangle',
        brandColor: '#007bff',
        position: { x: 50, y: 50 },
        rotation: 0,
        animation: {
          enabled: false,
          animationType: 'bounce',
          speed: 'medium'
        }
      })
    },
    recipientName: {
      type: String,
      default: ''
    },
    previewMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      typewriterProgress: 0,
      typewriterInterval: null,
      handles:['tl', 'tm', 'tr', 'mr', 'br', 'bm', 'bl', 'ml'],
      parent:{width:0,height:0}
    };
  },
  computed: {
    ...mapState({
      isGifMode: state => state.isGifMode,
      shouldPreviewGif: state => state.shouldPreviewGif
    }),
    displayText() {
      let text = this.overlaySettings.text || 'Add text here';

      // Replace placeholder with recipient name if provided
      if (this.recipientName) {
        text = text.replace(/\[FirstName\]/g, this.recipientName);
      }

      // For typewriter animation in non-GIF mode, show partial text
      if (this.overlaySettings.animation.enabled &&
          this.overlaySettings.animation.animationType === 'typewriter' &&
          !this.isGifMode) {
        return text.substring(0, this.typewriterProgress);
      }

      return text;
    },

    overlayClass() {
      const classes = ['overlay'];
      classes.push(`shape-${this.overlaySettings.shape}`);
      classes.push(`style-${this.overlaySettings.style}`);
      if (this.previewMode) classes.push('preview-mode');
      return classes.join(' ');
    },

    animationClass() {
      if (!this.overlaySettings.animation.enabled || this.isGifMode) return '';

      const animationClasses = [];
      const type = this.overlaySettings.animation.animationType;
      const speed = this.overlaySettings.animation.speed;

      animationClasses.push(`animate-${type}`);
      animationClasses.push(`speed-${speed}`);

      return animationClasses.join(' ');
    },

    overlayStyle() {
      const style = {
        fontSize: `${this.overlaySettings.fontSize}px`,
        left: `${this.overlaySettings.position.x}%`,
        top: `${this.overlaySettings.position.y}%`,
        transform: `translate(-50%, -50%) rotate(${this.overlaySettings.rotation}deg)`,
        cursor: this.previewMode ? 'default' : 'move'
      };

      // Apply style-specific styles
      if (this.overlaySettings.style === 'branded') {
        style.backgroundColor = this.overlaySettings.brandColor;
        style.color = 'white';
        style.border = `2px solid ${this.adjustColor(this.overlaySettings.brandColor, -20)}`;
      }

      return style;
    }
  },
  watch: {
    'overlaySettings.animation': {
      deep: true,
      handler(newVal) {
        // Reset and restart typewriter animation when animation settings change
        if (newVal.enabled && newVal.animationType === 'typewriter') {
          this.setupTypewriterAnimation();
        } else {
          this.clearTypewriterAnimation();
        }
      }
    }
  },
  mounted() {
    // Setup typewriter animation if needed
    if (this.overlaySettings.animation.enabled &&
        this.overlaySettings.animation.animationType === 'typewriter') {
      this.setupTypewriterAnimation();
    }
    if(this.hideHandles){
      this.handles = [];
    }
    // this.updateParentSize();
  },
  beforeDestroy() {
    this.clearTypewriterAnimation();
    window.removeEventListener('resize', this.updateParentSize);
  },
  methods: {
    ...mapMutations('imageEditor',[
      'SET_LAYER_DRAG',
      'SET_LAYER_RESIZE'
    ]),
    updateParentSize() {
      this.parent.width = this.$refs.imageContainer.offsetWidth;
      this.parent.height = this.$refs.imageContainer.offsetHeight;
    },
    hexToRgba(hex, alpha) {
      if (!hex) return 'transparent';
      hex = hex.replace('#', '');

      if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
      }

      const bigint = parseInt(hex, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;

      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    downloadCombinedImage() {
      const element = this.$refs.imageContainer;

      if (!element) {
        console.error('Image container not found');
        return;
      }

      toPng(element, { cacheBust: true })
          .then(dataUrl => {
            const link = document.createElement('a');
            link.download = 'overlay-image.png';
            link.href = dataUrl;
            link.click();
          })
          .catch(err => {
            console.error('Error generating image:', err);
          });
    },
    startDrag(e) {
      if (this.previewMode) return;

      this.isDragging = true;

      // Calculate offset from the center of the overlay
      const overlay = this.$refs.overlayText;
      const overlayRect = overlay.getBoundingClientRect();
      this.dragOffset.x = (overlayRect.left + overlayRect.width / 2) - e.clientX;
      this.dragOffset.y = (overlayRect.top + overlayRect.height / 2) - e.clientY;

      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);

      // Prevent text selection while dragging
      e.preventDefault();
    },

    onDrag(e) {
      if (!this.isDragging) return;

      const rect = this.$refs.imageContainer.getBoundingClientRect();
      const x = (e.clientX + this.dragOffset.x - rect.left) / rect.width * 100;
      const y = (e.clientY + this.dragOffset.y - rect.top) / rect.height * 100;

      // Ensure position stays within bounds (with some padding)
      const boundedX = Math.max(5, Math.min(95, x));
      const boundedY = Math.max(5, Math.min(95, y));

      this.$emit('position-change', { x: boundedX, y: boundedY });
    },

    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },

    exitPreviewMode() {
      this.$emit('exit-preview');
    },

    setupTypewriterAnimation() {
      this.clearTypewriterAnimation();
      this.typewriterProgress = 0;

      const text = this.overlaySettings.text || 'Add text here';
      const speed = this.getTypewriterSpeed();

      this.typewriterInterval = setInterval(() => {
        if (this.typewriterProgress < text.length) {
          this.typewriterProgress++;
        } else {
          // Reset animation after a pause
          setTimeout(() => {
            this.typewriterProgress = 0;
          }, 1000);
        }
      }, speed);
    },

    clearTypewriterAnimation() {
      if (this.typewriterInterval) {
        clearInterval(this.typewriterInterval);
        this.typewriterInterval = null;
      }
    },

    getTypewriterSpeed() {
      // Convert animation speed to milliseconds
      switch(this.overlaySettings.animation.speed) {
        case 'slow': return 200;
        case 'medium': return 100;
        case 'fast': return 50;
        default: return 100;
      }
    },

    // Utility to darken/lighten colors for borders, etc.
    adjustColor(color, amount) {
      // Remove the # if present
      let hex = color.replace('#', '');

      // Parse the color
      let r = parseInt(hex.substring(0, 2), 16);
      let g = parseInt(hex.substring(2, 4), 16);
      let b = parseInt(hex.substring(4, 6), 16);

      // Adjust each component
      r = Math.max(0, Math.min(255, r + amount));
      g = Math.max(0, Math.min(255, g + amount));
      b = Math.max(0, Math.min(255, b + amount));

      // Convert back to hex
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }
  }
};
</script>

<style scoped lang="scss">
.vdr{
  display: flex;
  justify-content: center;
  align-items: center;
  p{
    margin-bottom: 0;
  }
  border: 0;
}
.image-preview {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.preview-container {
  position: relative;
  width: 100%;
  background-color: #f8f9fa;
}

.image-container {
  position: relative;
  width: 100%;
}

.preview-image {
  width: 100%;
  height: auto;
  display: block;
}

.overlay-text {
  position: absolute;
  padding: 10px 15px;
  border-radius: 5px;
  user-select: none;
  max-width: 80%;
  word-wrap: break-word;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.1s ease;
  z-index: 10;
}

.overlay-text.preview-mode {
  cursor: default;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.drag-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  border: 2px dotted rgba(0, 123, 255, 0.3);
  z-index: 5;
}

.drag-handle-dots {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-image: radial-gradient(circle, rgba(0, 123, 255, 0.5) 1px, transparent 2px);
  background-size: 8px 8px;
  pointer-events: none;
}

.move-indicator {
  position: absolute;
  width: 20px;
  height: 20px;
  margin-left: -10px;
  margin-top: -10px;
  border-radius: 50%;
  background-color: rgba(0, 123, 255, 0.4);
  border: 2px solid rgba(0, 123, 255, 0.8);
  z-index: 5;
  pointer-events: none;
  animation: pulse 2s infinite;
}

.handle-tooltip {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
}

.move-indicator:hover .handle-tooltip {
  opacity: 1;
}

.preview-controls {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 20;
}

.no-image {
  background-color: #f8f9fa;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  border: 1px dashed #ced4da;
}

.no-image-icon {
  font-size: 3rem;
  margin-top: 0.5rem;
  opacity: 0.5;
}

/* Style-specific styles */
.style-white {
  background-color: white;
  color: #212529;
  border: 1px solid #e9ecef;
}

.style-translucent {
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  backdrop-filter: blur(2px);
}

/* Shape-specific styles */
.shape-rectangle {
  border-radius: 5px;
}

.shape-circle {
  border-radius: 50%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px;
}

.shape-speech-left {
  border-radius: 12px;
}

.shape-speech-left::before {
  content: "";
  position: absolute;
  bottom: -25px;
  left: 20px;
  width: 30px;
  height: 25px;
  background-color: inherit;
  clip-path: polygon(0 0, 100% 0, 0 100%);
}

.style-white.shape-speech-left::before {
  border-right-color: white;
}

.style-translucent.shape-speech-left::before {
  border-right-color: rgba(0, 0, 0, 0.6);
}

.shape-speech-right {
  border-radius: 12px;
}

.shape-speech-right::after {
  content: "";
  position: absolute;
  bottom: -25px;
  right: 20px;
  width: 30px;
  height: 25px;
  background-color: inherit;
  clip-path: polygon(0 0, 100% 0, 100% 100%);
}

.style-white.shape-speech-right::after {
  border-left-color: white;
}

.style-translucent.shape-speech-right::after {
  border-left-color: rgba(0, 0, 0, 0.6);
}

.shape-ribbon {
  border-radius: 0;
  padding-top: 5px;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 35px;
  clip-path: polygon(0% 0%, 100% 0%, 100% 70%, 75% 70%, 75% 100%, 50% 85%, 25% 100%, 25% 70%, 0% 70%);
}

/*.shape-ribbon::before,
.shape-ribbon::after {
  content: '';
  position: absolute;
  bottom: -10px;
  border-top: 10px solid rgba(0, 0, 0, 0.2);
  z-index: -1;
}

.shape-ribbon::before {
  left: 0;
  border-right: 10px solid transparent;
}

.shape-ribbon::after {
  right: 0;
  border-left: 10px solid transparent;
}*/

.shape-hexagon {
  clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  padding: 20px;
}

/* Animation classes */
.animate-bounce {
  animation: bounce 2s infinite;
}

.animate-fade {
  animation: fade 2s infinite;
}

.animate-rotate {
  animation: rotate 3s infinite linear;
}

.animate-pulse {
  animation: pulse 2s infinite;
}

/* Animation speed modifiers */
.speed-slow {
  animation-duration: 3s;
}

.speed-medium {
  animation-duration: 2s;
}

.speed-fast {
  animation-duration: 1s;
}

/* Animation keyframes */
@keyframes bounce {
  0%, 100% {
    transform: translate(-50%, -50%) rotate(var(--rotation, 0deg)) translateY(0);
  }
  50% {
    transform: translate(-50%, -50%) rotate(var(--rotation, 0deg)) translateY(-10px);
  }
}

@keyframes fade {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

@keyframes rotate {
  from {
    transform: translate(-50%, -50%) rotate(calc(var(--rotation, 0deg) + 0deg));
  }
  to {
    transform: translate(-50%, -50%) rotate(calc(var(--rotation, 0deg) + 360deg));
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* GIF animation preview overlay */
.animation-preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.animation-message {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}
</style>
